import { featuresExtent } from '@openlayers/ol-utils';

import OlMap from 'ol/Map';
import Feature from 'ol/Feature';
import { LineString, Polygon as OlPolygon } from 'ol/geom';
import * as olStyle from 'ol/style';
import * as olExtent from 'ol/extent';
import { transform as olTransform } from 'ol/proj';
import OlOverlay from 'ol/Overlay';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import OlSelect from 'ol/interaction/Select';
import { MassGridCellMouseInteraction } from '@pages/map/ui/mass-grid/MassGridCellMouseInteraction';

export class MassGridLayer {
    private readonly gridLayerSource = new VectorSource({
        features: [],
        useSpatialIndex: true,
    });
    private readonly gridLayer = new VectorLayer({
        source: this.gridLayerSource,
        updateWhileAnimating: true,
        updateWhileInteracting: true,
    });
    private readonly selectInteraction = new OlSelect({
        layers: [this.gridLayer],
    });

    private readonly cellMouseInteraction = new MassGridCellMouseInteraction(this.mapInstance, this.calc);

    private readonly styles = {
        bb: new olStyle.Style({
            stroke: new olStyle.Stroke({
                color: [0, 0, 0, 0.7],
                width: 1,
            }),
        }),
        cell: new olStyle.Style({
            fill: new olStyle.Fill({
                color: [50, 50, 50, 0.02],
            }),
            stroke: new olStyle.Stroke({
                color: [50, 50, 50, 0.3],
                width: 1,
            }),
        }),
        line: new olStyle.Style({
            stroke: new olStyle.Stroke({
                color: [50, 220, 50, 0.8],
                width: 1,
            }),
        }),
        lineClickArea: new olStyle.Style({
            stroke: new olStyle.Stroke({
                color: [0, 0, 0, 0.025],
                width: 10,
            }),
        }),
    };
    private readonly labels: OlOverlay[] = [];
    private readonly labelsCache: { [key: string]: OlOverlay } = {};
    private bbExtent;

    private readonly calcByStation = {};

    constructor(private mapInstance: OlMap, private calc: Mass.AreaCalculation = null, private showLabels = false, private showCrossSectionLines = false) {
        this.gridLayer.setZIndex(45);
        mapInstance.addLayer(this.gridLayer);

        this.selectInteraction.getFeatures().on('add', e => {
            const data = e.element.get('data');

            if (e.element.get('type') == 'line') {
                const geom = (e.element.getGeometry() as LineString).getCoordinates();
                const p1 = olTransform(geom[0], 'EPSG:3857', 'EPSG:4326');
                const p2 = olTransform(geom[1], 'EPSG:3857', 'EPSG:4326');
                window.open('free_cross_section.htm?projectId=' + kuura.getProjectId() + '&lat1=' + p1[1] + '&lon1=' + p1[0] + '&lat2=' + p2[1] + '&lon2=' + p2[0], '_blank');
            }
        });
        mapInstance.addInteraction(this.selectInteraction);

        if (this.calc && this.calc.rows) {
            this.calc.rows.forEach(row => (this.calcByStation[row.station] = row));
        }
    }

    destroy = () => {
        this.clearMap();
        this.mapInstance.removeLayer(this.gridLayer);
        this.mapInstance.removeInteraction(this.selectInteraction);
    };

    clearMap = () => {
        this.gridLayerSource.clear();
        this.labels.forEach(l => this.mapInstance.removeOverlay(l));
        this.labels.length = 0;
    };

    createLabel(center, text): OlOverlay {
        if (!this.labelsCache[text]) {
            this.labelsCache[text] = new OlOverlay({
                element: $('<div class="mass-grid-cell-label">' + text + '</div>')[0] as HTMLDivElement,
                positioning: 'center-center',
                stopEvent: false,
            });
        }

        const label = this.labelsCache[text];
        this.labels.push(label);
        label.setPosition(center);
        this.mapInstance.addOverlay(label);
        return label;
    }

    getBoundingBoxMeasures = (models: Feature[]) => {
        const boundingBox = featuresExtent(models, false);
        if (olExtent.isEmpty(boundingBox)) {
            throw new Error('got empty extent for models');
        }

        const [x, y, x2, y2] = boundingBox;
        const diagonal = Math.sqrt(Math.pow(x2 - x, 2) + Math.pow(y2 - y, 2)) / 2;
        const centerCoords = olExtent.getCenter(boundingBox);

        const feature = new Feature({
            geometry: new OlPolygon([
                [
                    [x, y],
                    [x, y2],
                    [x2, y2],
                    [x2, y],
                ],
            ]),
        });
        feature.setStyle(this.styles.bb);

        return {
            boundingBox,
            diagonal,
            x,
            y,
            x2,
            y2,
            center: {
                x: centerCoords[0],
                y: centerCoords[1],
            },
            centerCoords,
            feature,
        };
    };

    draw = _.throttle((size: number, rotation: number, modelsSource: VectorSource) => {
        this.clearMap();

        const angle = (-rotation * Math.PI) / 180.0;
        const meter = size;
        console.log('draw start - size:', meter, 'rotation:', rotation, 'resolution');

        let bbModels;
        let bbOuter;

        try {
            // todo cache whole block outside of draw
            bbModels = this.getBoundingBoxMeasures(modelsSource.getFeatures());
            this.bbExtent = bbModels.boundingBox;
            const outerBBoxFeature = new Feature({
                geometry: new OlPolygon([
                    [
                        [bbModels.center.x - bbModels.diagonal, bbModels.center.y + bbModels.diagonal],
                        [bbModels.center.x + bbModels.diagonal, bbModels.center.y + bbModels.diagonal],
                        [bbModels.center.x + bbModels.diagonal, bbModels.center.y - bbModels.diagonal],
                        [bbModels.center.x - bbModels.diagonal, bbModels.center.y - bbModels.diagonal],
                    ],
                ]),
            });
            bbOuter = this.getBoundingBoxMeasures([outerBBoxFeature]);

            //outerBBoxFeature.setStyle(this.styles.bb);
            //this.gridLayerSource.addFeature(outerBBoxFeature);
        } catch {
            console.error('Grid draw - featuresExtent returned empty extent, models:', modelsSource);
            return;
        }

        let totalCells = 0;
        const origin = bbOuter;
        let cx = origin.x;
        for (let i = 0; cx < origin.x2; i++) {
            let cy = origin.y;
            let cx2;
            let cy2;

            for (let j = 0; cy < origin.y2; j++) {
                [cx, cy] = offsetNE(origin.x, origin.y, meter * i, meter * j);
                [cx2, cy2] = offsetNE(cx, cy, meter, meter);

                // first make geometry to check for intersections
                const geometry = new OlPolygon([
                    [
                        [cx, cy],
                        [cx, cy2],
                        [cx2, cy2],
                        [cx2, cy],
                    ],
                ]);
                geometry.rotate(angle, bbModels.centerCoords);

                // check if cell intersects all 3 models bounding box
                if (!geometry.intersectsExtent(bbModels.boundingBox)) {
                    continue;
                }

                // check if cell intersects at least two models
                let intersectsCount = 0;
                modelsSource.forEachFeatureIntersectingExtent(geometry.getExtent(), f => {
                    intersectsCount += 1;
                });
                if (intersectsCount <= 1) continue;

                // make feature
                const cell = new Feature({
                    geometry,
                });
                cell.set('mouseInteraction', this.cellMouseInteraction);

                // cache ajax data
                const [p1, p2, p3, p4] = geometry.getCoordinates()[0];
                cell.set('type', 'cell');
                cell.set('data', {
                    x: i,
                    y: j,
                    p1,
                    p2,
                    p3,
                    p4,
                });

                cell.setStyle(this.styles.cell);
                this.gridLayerSource.addFeature(cell);
                totalCells++;

                if (this.showLabels) {
                    this.createLabel(olExtent.getCenter(geometry.getExtent()), j + ',' + i);
                }
            }
        }

        if (this.showCrossSectionLines) {
            setTimeout(() => {
                this.drawCrossSectionLines();
            }, 150);
        }

        return totalCells;
    }, 100);

    drawCrossSectionLines = () => {
        const cells = [];
        this.gridLayerSource.forEachFeature(cell => {
            if (cell.get('type') == 'cell') {
                cells.push(cell.get('data'));
            }
        });

        const rows = _.groupBy(cells, c => c.y);

        const lines = Object.entries(rows).forEach(([key, val]) => {
            val.sort((c1, c2) => c1.x - c2.x);

            const line = new Feature({
                geometry: new LineString([val[0].p1, val[val.length - 1].p4]),
            });

            line.set('type', 'line');
            line.setStyle([this.styles.line, this.styles.lineClickArea]);
            this.gridLayerSource.addFeature(line);
        });
    };

    getState = () => {
        const cells = [];
        this.gridLayerSource.forEachFeature(cell => {
            if (cell.get('type') == 'cell') {
                cells.push(cell.get('data'));
            }
        });

        return {
            cells,
            bbExtent: this.bbExtent,
        };
    };

    setLabels = state => {
        this.showLabels = state;
    };

    setCrossSectionLines(state: boolean) {
        this.showCrossSectionLines = state;
    }
}

function offsetNE(n, e, x, y) {
    const latLon = olTransform([n, e], 'EPSG:3857', 'EPSG:4326');
    const result = offsetHaversine(latLon[1], latLon[0], x, y);
    return olTransform([result[1], result[0]], 'EPSG:4326', 'EPSG:3857');
}

function offsetHaversine(aLat, aLng, x, y) {
    const R = 6378137;
    const dLat = y / R;
    const dLng = x / (R * Math.cos((aLat * Math.PI) / 180.0));
    return [aLat + (dLat * 180.0) / Math.PI, aLng + (dLng * 180.0) / Math.PI];
}
